import { useEffect, useState } from 'react';
import { ROUTES } from "../App";
import { ICONS } from "../constants/icons";
import { LuEyeOff as FaRegEyeSlash, LuEye as FaRegEye } from "react-icons/lu";
import { signInWithCredential, signInWithEmailAndPassword } from "firebase/auth";
import { auth, database } from "../constants/firebase";
import {
    GoogleAuthProvider,
    OAuthProvider,
    signInWithPopup
} from "firebase/auth";
import { ref, get, set } from "firebase/database";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { createSession, saveToken } from "../utils/utils";
import { useLocation } from "react-router";


const SignIn = () => {
    const navigate = useMoveTo();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { refreshUser, currentUser, loading: userLoading } = useUser();
    const [googleLoading, setGoogleLoading] = useState(false);
    const [appleLoading, setAppleLoading] = useState(false);
    const isIOS = window.isIOS;
    const isAndroid = window.isAndroid;
    const isDesktop = !isIOS && !isAndroid;

    // ✅ extract redirect param
    const queryParams = new URLSearchParams(location.search);
    const redirectPath = queryParams.get("redirect") || ROUTES.DASHBOARD;


    useEffect(() => {
        if (!userLoading && currentUser) {
            navigate(redirectPath, { replace: true });
        }
    }, [currentUser, userLoading, navigate, redirectPath]);

    useEffect(() => {
        if (location.state && location.state.message) {
            setError(location.state.message);
        }
    }, [location.state]);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            localStorage.setItem("uid", user.uid);
            await refreshUser();
            // ✅ create new sessionId (kicks old session)
            await createSession(user);
            // ✅ save FCM token only for this session
            await saveToken(user);
            // 👇 Send message to React Native app if inside WebView
            if (window.isInApp) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: "USER_LOGGED_IN",
                        uid: user.uid,
                        email: user.email,
                    })
                );
            }
            setTimeout(() => {
                navigate(redirectPath, { replace: true });
            }, 800);
        } catch (err) {
            console.error("Login failed:", err.message);
            setError("Invalid email or password. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setGoogleLoading(true); // start loader
        if (window.isInApp) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({ type: "GOOGLE_LOGIN" })
            );
        } else {
            const provider = new GoogleAuthProvider();
            try {
                const result = await signInWithPopup(auth, provider);
                await processFirebaseUser(result.user);
            } catch (err) {
                console.error("Google login failed:", err.message);
            } finally {
                setGoogleLoading(false); // stop loader
            }
        }
    };


    // Listen for native Google login result from WebView
    useEffect(() => {
        const handleMessage = async (event) => {
            try {
                const message = JSON.parse(event.data);

                if (message.type === "GOOGLE_LOGIN_SUCCESS") {
                    const credential = GoogleAuthProvider.credential(message.token);
                    const result = await signInWithCredential(auth, credential);
                    await processFirebaseUser(result.user);
                    setGoogleLoading(false);
                }

                if (message.type === "GOOGLE_LOGIN_ERROR") {
                    console.error("Native Google login failed:", message.error);
                    setGoogleLoading(false);
                }
            } catch { }
        };

        if (window.isInApp) {
            document.addEventListener('message', handleMessage);  // Android
            window.addEventListener('message', handleMessage);  // iOS
            return () => {
                document.removeEventListener("message", handleMessage);
                window.removeEventListener('message', handleMessage);
            };
        }
    }, []);

    // Process user after login
    const processFirebaseUser = async (user) => {
        const userRef = ref(database, "Users/" + user.uid);
        const snapshot = await get(userRef);

        if (!snapshot.exists()) {
            navigate(ROUTES.SIGN_UP, {
                state: {
                    isGoogleSignUp: true,
                    uid: user.uid,
                    name: user.displayName || "",
                    email: user.email || "",
                    profilePic: user.photoURL || "",
                },
            });
        } else {
            localStorage.setItem("uid", user.uid);
            await refreshUser();
            // ✅ create new sessionId (kicks old session)
            await createSession(user);
            // ✅ save FCM token only for this session
            await saveToken(user);

            // 👇 Send message to React Native app if inside WebView
            if (window.isInApp) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: "USER_LOGGED_IN",
                        uid: user.uid,
                        email: user.email,
                    })
                );
            }
            setTimeout(() => {
                navigate(redirectPath, { replace: true });
            }, 800);
        }
    };

    // Process user after apple login
    const processAppleUser = async (user) => {
        const userRef = ref(database, "Users/" + user.uid);
        const snapshot = await get(userRef);

        if (!snapshot.exists()) {
            await set(ref(database, 'Users/' + user.uid), {
                uid: user.uid,
                name: user.displayName || "Zylen User",
                username: user.email.split('@')[0],
                about: "",
                email: user.email || "",
                profilePic: user.photoURL || "",
                niche: 'Filmmaker',
                role: 'Free',
                isTeamUser: false,
                provider: "apple",
                createdAt: new Date().toISOString()
            });
            localStorage.setItem("uid", user.uid);
            await refreshUser();
            // ✅ create new sessionId (kicks old session)
            await createSession(user);
            // ✅ save FCM token only for this session
            await saveToken(user);
            // 👇 Send message to React Native app if inside WebView
            if (window.isInApp) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: "USER_LOGGED_IN",
                        uid: user.uid,
                        email: user.email,
                    })
                );
            }
            setTimeout(() => {
                navigate(redirectPath, { replace: true });
            }, 800);
        }
        else {
            localStorage.setItem("uid", user.uid);
            await refreshUser();
            // ✅ create new sessionId (kicks old session)
            await createSession(user);
            // ✅ save FCM token only for this session
            await saveToken(user);
            // 👇 Send message to React Native app if inside WebView
            if (window.isInApp) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: "USER_LOGGED_IN",
                        uid: user.uid,
                        email: user.email,
                    })
                );
            }
            setTimeout(() => {
                navigate(redirectPath, { replace: true });
            }, 800);
        }
    };

    const handleAppleLogin = async () => {
        setAppleLoading(true);
        if (window.isInApp) {
            // Inside React Native WebView → request native Apple login
            window.ReactNativeWebView.postMessage(
                JSON.stringify({ type: "APPLE_LOGIN" })
            );
        } else {
            // Normal Web login
            const provider = new OAuthProvider("apple.com");
            try {
                const result = await signInWithPopup(auth, provider);
                await processAppleUser(result.user);
            } catch (err) {
                console.error("Apple login failed:", err.message);
            } finally {
                setAppleLoading(false); // stop loader (web flow)
            }
        }
    };

    // Listen for native Apple login result from WebView
    useEffect(() => {
        const handleMessage = async (event) => {
            // Filter out unwanted messages
            if (!event.data) return;

            // Ignore non-string data
            if (typeof event.data !== "string") return;

            // Ignore Firebase/Google Identity internal messages
            if (event.data.startsWith("!_")) return;

            // Ignore MetaMask and other extension messages
            if (event.data.includes("metamask") || event.data.includes("target")) return;

            try {
                const message = JSON.parse(event.data);

                // Only process Apple login messages
                if (message.type === "APPLE_LOGIN_SUCCESS") {
                    const credential = new OAuthProvider("apple.com").credential({
                        idToken: message.identityToken,
                        rawNonce: message.rawNonce,
                    });
                    const result = await signInWithCredential(auth, credential);
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({ type: "RESULT", user: result.user })
                    );
                    await processAppleUser(result.user);
                    setAppleLoading(false);
                }

                if (message.type === "APPLE_LOGIN_ERROR") {
                    console.error("Native Apple login failed:", message.error);
                    setAppleLoading(false);
                }
            } catch (err) {
                // Silently ignore parsing errors from unrelated messages
                return;
            }
        };

        if (window.isInApp) {
            document.addEventListener('message', handleMessage);  // Android
            window.addEventListener('message', handleMessage);  // iOS
            return () => {
                document.removeEventListener("message", handleMessage);
                window.removeEventListener('message', handleMessage);
            };
        }
    }, []);



    return (
        <div className="bg-main w-full flex items-center justify-center px-4 sm:px-6 md:px-8 py-12">
            <div className="w-full max-w-md mt-20">
                {/* Header */}
                <div className="text-center mb-8">
                    <h3 className="text-2xl md:text-4xl font-bold dark:text-white text-black mb-2">
                        Sign In
                    </h3>
                    <p className="text-sm md:text-base dark:text-gray-300 text-gray-700">
                        Welcome back! Please log in to continue.
                    </p>
                    {error && <p className="text-red-500 text-sm mt-3">{error}</p>}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Email */}
                    <div>
                        <label htmlFor="email" className="sr-only">Email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="creator@mail.com"
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0"
                            required
                        />
                    </div>

                    {/* Password */}
                    <div className="relative">
                        <label htmlFor="password" className="sr-only">Password</label>
                        <input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Password"
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 pr-10"
                            required
                        />
                        <button
                            type="button"
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black"
                            onClick={togglePasswordVisibility}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? (
                                <FaRegEye size={18} className="text-black" />
                            ) : (
                                <FaRegEyeSlash size={18} className="text-black" />
                            )}
                        </button>
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="button"
                            className="text-sm dark:text-white text-gray-700"
                            onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
                        >
                            Forgot Password?
                        </button>
                    </div>

                    {/* Submit */}
                    <button type="submit" className="btn-primary w-full">
                        {loading ? "Logging in..." : "Log In"}
                    </button>
                </form>

                {/* Social Login */}
                <div className="mt-6">
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-700" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-main text-gray-400">Or</span>
                        </div>
                    </div>

                    <div className="mt-6 flex flex-col gap-3">
                        {/* Google */}
                        <button t
                            ype="button"
                            className="btn-secondary w-full items-center"
                            onClick={handleGoogleLogin}
                        >
                            <img src={ICONS.google} alt="Google" className="w-5 h-5" />
                            <span className="ml-2 text-light-purple">
                                {googleLoading ? "Signing in..." : "Login with Google"}
                            </span>
                        </button>

                        {/* Apple (only show on iOS + Desktop, hide on Android) */}
                        {(isIOS || isDesktop) && (
                            <button
                                type="button"
                                className="btn-secondary w-full"
                                onClick={handleAppleLogin}
                            >
                                <img src={ICONS.apple} alt="Apple" className="w-5 h-5" />
                                <span className="ml-2 text-light-purple">
                                    {appleLoading ? "Signing in..." : "Login with Apple"}
                                </span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Sign Up */}
                <div className="mt-5 text-center">
                    <p className="dark:text-white text-[16px] text-gray-700">
                        Don’t have an account?{' '}
                        <button onClick={() => navigate(ROUTES.SIGN_UP)} className="text-primary font-medium">
                            Sign Up
                        </button>
                    </p>
                </div>
            </div>
        </div>

    );
};

export default SignIn;